import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { t } from 'i18next';
import { useCreateScene } from '../../../domains/scene/ui/hooks/useCreateScene';
import {
  SCENE_READY_POLL_INTERVAL_MS,
  useGetSceneDetail
} from '../../../domains/scene/ui/hooks/useGetSceneDetail';
import { useGetUserIdentities } from '../../../domains/user/ui/hooks/useGetUserIdentities';
import { useSuccessPhotoCardToast } from '../../template/edit-collected-photo-cards/hooks/useSuccessPhotoCardToast';
import { getCastRelationshipId } from '../utils/getCastRelationshipId';
import { getAgeFromBirthday } from '../../identities/utils/getAgeFromBirthday';
import { openNewTab } from '../../../utils/UIUtils';
import { ROUTES } from '../../../constants/routes';
import type { components } from '../../../types/schema';

type AdaptedFictionPreview = components['schemas']['ApiV1AdaptedFictionPreview'];

const R19_AGE_RATING = 'R-19';
const MIN_AGE_FOR_R19 = 20;

export const useManageCreateScene = (preview: AdaptedFictionPreview) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [pendingSceneId, setPendingSceneId] = useState<string | null>(null);
  const castRelationshipId = getCastRelationshipId();
  const { data: userIdentity } = useGetUserIdentities();
  const showSuccessToast = useSuccessPhotoCardToast();

  const { mutateAsync: createScene } = useCreateScene();
  const { data: sceneDetail } = useGetSceneDetail(pendingSceneId ?? '', {
    enabled: !!pendingSceneId,
    refetchInterval: pendingSceneId ? SCENE_READY_POLL_INTERVAL_MS : false
  });

  useEffect(() => {
    if (
      pendingSceneId &&
      (sceneDetail?.status === 'preview_ready' || sceneDetail?.status === 'completed')
    ) {
      setPendingSceneId(null);
      navigate(ROUTES.SCENE.PREVIEW_DETAIL.GO(pendingSceneId));
    }
  }, [pendingSceneId, sceneDetail?.status, navigate]);

  const proceedToCreateScene = async () => {
    if (!castRelationshipId) return;
    if (preview?.sceneId) {
      navigate(
        `${ROUTES.SCENE.PREVIEW_DETAIL.GO(preview.sceneId)}${preview?.lastContentId ? `?cursor=${preview?.lastContentId}` : ''}`
      );
      return;
    }

    const requestBody = {
      scene: {
        cast_relationship_id: castRelationshipId,
        adapted_fiction_preview_id: preview.adaptedFictionPreviewId
      }
    };
    const response = await createScene(requestBody);
    if (response?.sceneId) {
      setPendingSceneId(response.sceneId);
    }
  };

  const handleCreateScene = async () => {
    if (!castRelationshipId) return;

    const isR19 = preview?.editionAgeRating === R19_AGE_RATING;

    if (isR19) {
      if (!userIdentity) {
        const returnTo = encodeURIComponent(location.pathname + location.search);
        openNewTab(`/identity-verification/intro?type=scene&return_to=${returnTo}`);
        return;
      }

      const age = getAgeFromBirthday('2001-03-13');

      if (age < MIN_AGE_FOR_R19) {
        showSuccessToast(
          'bottom',
          t('SceneMain.ageRestrictionToast') as string,
          false,
          React.createElement('img', {
            src: '/assets/icons/shield_account.png',
            alt: 'shield',
            width: 20,
            height: 20
          }),
          false
        );
        return;
      }
    }

    await proceedToCreateScene();
  };

  return {
    pendingSceneId,
    handleCreateScene,
    clearPendingScene: () => setPendingSceneId(null)
  };
};
